import { useMemo, useState } from "react";
import { api } from "./api";
import { ErrorNotice, PathwayMark } from "./components";
import type { InventoryContent, Letter, Participant } from "./types";

interface SummaryProps {
  content: InventoryContent;
  participant: Participant;
  onContinue?: () => void;
  onParticipantChange?: (participant: Participant) => void;
}

export function Summary({
  content,
  participant,
  onContinue,
  onParticipantChange,
}: SummaryProps) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const pathwayDetails = useMemo(
    () =>
      participant.primaryPathways
        .map((name) => Object.values(content.pathways).find((pathway) => pathway.name === name))
        .filter(Boolean),
    [content.pathways, participant.primaryPathways],
  );

  async function emailSummary() {
    setError("");
    setSending(true);
    try {
      const response = await api.emailResult(participant.resultToken);
      onParticipantChange?.(response.participant);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The email could not be sent.");
    } finally {
      setSending(false);
    }
  }

  return (
    <article className="summary-document">
      <header className="summary-hero">
        <p className="eyebrow">Your Redemptive Builder Summary</p>
        <h1>{participant.firstName} {participant.lastName}</h1>
        <p className="summary-meta">
          {participant.participantType} <span /> {participant.organization}
          {participant.profession ? <><span /> {participant.profession}</> : null}
        </p>
      </header>

      <section className="summary-section pathway-summary">
        <p className="eyebrow">Your Redemptive {participant.tieCount > 1 ? "Pathways" : "Pathway"}</p>
        <div className={`primary-pathways ${participant.tieCount > 1 ? "tied" : ""}`}>
          {pathwayDetails.map((pathway) => pathway && (
            <div className="primary-pathway" key={pathway.name}>
              <PathwayMark name={pathway.name} large />
              <div>
                <h2>{pathway.name}</h2>
                <p>{pathway.description}</p>
              </div>
            </div>
          ))}
        </div>
        {participant.tieCount >= 3 && (
          <p className="tie-note">Your responses revealed several equally strong pathways. Each one is shown as a co-primary result.</p>
        )}
        <details className="inventory-scoring-summary">
          <summary><span>View my Inventory &amp; scoring</span><span className="details-action" aria-hidden="true" /></summary>
          <div className="inventory-scoring-content">
            <section>
              <p className="eyebrow">How your result was calculated</p>
              <h3>All nine pathway scores</h3>
              <p className="scoring-note">Each response adds one point to its matching pathway. Your highest score identifies your primary pathway, and equal highest scores are shown together.</p>
              <div className="summary-score-grid">
                {(Object.entries(content.pathways) as [Letter, { name: string }][]).map(([letter, pathway]) => (
                  <div className={participant.primaryPathways.includes(pathway.name) ? "primary" : ""} key={letter}>
                    <PathwayMark name={pathway.name} />
                    <p><span>{letter}</span>{pathway.name}<strong>{participant.scores[letter] || 0}</strong></p>
                  </div>
                ))}
              </div>
            </section>
            <section>
              <p className="eyebrow">Your completed Inventory</p>
              <h3>Your 12 responses</h3>
              <div className="summary-answer-list">
                {content.questions.map((question) => {
                  const answer = participant.inventoryAnswers[String(question.number)];
                  return (
                    <article key={question.number}>
                      <span>{question.number}</span>
                      <div><p>{question.text}</p><strong>{answer ? `${answer.letter}. ${answer.text}` : "Not answered"}</strong></div>
                    </article>
                  );
                })}
              </div>
            </section>
          </div>
        </details>
      </section>

      <section className="summary-section journey-summary">
        <div className="section-heading">
          <span>01</span>
          <div><p className="eyebrow">Your reflections</p><h2>Your Builder Journey</h2></div>
        </div>
        <div className="reflection-list">
          {content.journeyQuestions.map((question) => {
            const answer = participant.journeyAnswers[String(question.number)];
            return (
              <div className="reflection" key={question.number}>
                <p className="reflection-question">{question.text}</p>
                {question.kind === "passions" ? (
                  <ol>{answer?.passions.map((passion) => <li key={passion}>{passion}</li>)}</ol>
                ) : (
                  <p className="reflection-answer">{answer?.text}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="invitation-callout">
        <p className="eyebrow">Where is God inviting you to begin?</p>
        <h2>You have a unique role to play in God's redemptive story.</h2>
        <p>Building Redemptive Community means engaging in the places God has already put you, your workplace, neighborhood, church, relationships, or community, and asking how He is inviting you to participate in His work there.</p>
        <p className="closing-question">Where is He inviting you to begin?</p>
      </section>

      <ErrorNotice message={error} />
      <div className="summary-actions no-print">
        <button className="button secondary" type="button" onClick={() => window.print()}>
          Save / print my summary
        </button>
        <button className="button secondary" type="button" disabled={sending} onClick={emailSummary}>
          {sending ? "Sending..." : participant.emailStatus === "sent" ? "Email my summary again" : "Email my summary"}
        </button>
        {onContinue && <button className="button" type="button" onClick={onContinue}>Continue</button>}
      </div>
      <p className="email-status no-print">
        {participant.emailStatus === "sent"
          ? `A summary link was sent to ${participant.email}.`
          : participant.emailStatus === "failed"
            ? participant.emailError
            : "Your summary remains available on this page."}
      </p>
    </article>
  );
}
